import React, { useRef, useState, useEffect } from "react";
import {
  Grid,
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContentText,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import emailjs from "emailjs-com";
import EmailImage from "../../Assets/support_img.png";
import DownloadIcon from "@mui/icons-material/Download";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CircularProgress from "@mui/material/CircularProgress";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import QRCode from "qrcode.react";
import { Link } from "@mui/material";
import useHttp from "../../Hooks/use-http";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";

import UploadApp from "./UploadApp";
import PortalTitle from "../CommonComponent/PortalTitle";

const errorInit = {
  fullNameError: "",
  fromEmailError: "",
  subjectError: "",
  messageError: "",
};

// const QrCodeWithReveal = ({ value, title, link }) => {
//   const [blurred, setBlurred] = useState(true);
//   const [timeoutId, setTimeoutId] = useState(null);

//   const handleReveal = () => {
//     setBlurred(false);

//     // Clear any existing timeout
//     if (timeoutId) clearTimeout(timeoutId);

//     // Set new timeout to blur after 10 seconds
//     const id = setTimeout(() => setBlurred(true), 10000);
//     setTimeoutId(id);
//   };

//   // Clean up timeout on component unmount
//   useEffect(() => {
//     return () => {
//       if (timeoutId) clearTimeout(timeoutId);
//     };
//   }, [timeoutId]);

//   return (
//     <Box
//       textAlign="center"
//       sx={{
//         position: "relative",
//         transition: "transform 0.3s",
//         "&:hover": { transform: "translateY(-5px)" },
//       }}
//     >
//       {/* QR Code Container with blur effect */}
//       <Box
//         sx={{
//           filter: blurred ? "blur(4px)" : "none",
//           transition: "filter 0.3s ease",
//         }}
//       >
//         <QRCode value={value} size={180} />
//       </Box>

//       {/* Reveal Button - positioned absolutely but not blurred */}
//       {blurred && (
//         <Box
//           sx={{
//             position: "absolute",
//             top: "50%",
//             left: "50%",
//             transform: "translate(-50%, -50%)",
//           }}
//         >
//           <Button
//             variant="contained"
//             color="primary"
//             onClick={handleReveal}
//             sx={{
//               boxShadow: 3,
//               "&:hover": { boxShadow: 5 },
//             }}
//           >
//             Show QR
//           </Button>
//         </Box>
//       )}

//       <Link
//         href={link}
//         target="_blank"
//         rel="noopener noreferrer"
//         sx={{
//           textDecoration: "none",
//           "&:hover": { textDecoration: "underline" },
//           display: "block",
//           mt: 1,
//         }}
//       >
//         {/* <Typography color="primary.main" fontWeight="medium">
//           {title?.charAt(0)?.toUpperCase() + title?.slice(1)}
//         </Typography> */}
//           <Box display="flex" alignItems="center" justifyContent="center">
//           <Typography color="primary.main" fontWeight="medium">
//             {title?.charAt(0)?.toUpperCase() + title?.slice(1)}
//           </Typography>
//         </Box>
//       </Link>
//     </Box>
//   );
// };

const QrCodeWithReveal = ({ value, title, link, versionName, titleInfo }) => {
  const [blurred, setBlurred] = useState(true);
  const [timeoutId, setTimeoutId] = useState(null);

  const handleReveal = () => {
    setBlurred(false);

    if (timeoutId) clearTimeout(timeoutId);

    const id = setTimeout(() => setBlurred(true), 10000);
    setTimeoutId(id);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [timeoutId]);

  const handleDownloadClick = () => {
    window.open(link, "_blank", "noopener,noreferrer");
  };

  const handleCopyLink = async () => {
    if (!link) {
      toast.error("No download link available");
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      toast.success("Download link copied");
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  return (
    <Box
      textAlign="center"
      sx={{
        position: "relative",
        transition: "transform 0.3s",
        "&:hover": { transform: "translateY(-5px)" },
      }}
    >
      <Box
        sx={{
          filter: blurred ? "blur(4px)" : "none",
          transition: "filter 0.3s ease",
        }}
      >
        <QRCode value={value} size={180} />
      </Box>

      {blurred && (
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={handleReveal}
            sx={{
              boxShadow: 3,
              "&:hover": { boxShadow: 5 },
            }}
          >
            Show QR
          </Button>
        </Box>
      )}

      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        justifyContent="center"
        flexWrap="nowrap"
        mt={1.5}
        gap={1}
        sx={{ width: "100%" }}
      >
        <Tooltip
          title={versionName ? `Version: ${versionName}` : ""}
          arrow
          placement="top"
          disableHoverListener={!versionName}
        >
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadClick}
            sx={{
              background: "linear-gradient(135deg, #3B9FD9 0%, #1D85C6 100%)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "12px",
              letterSpacing: "0.2px",
              px: 1.2,
              py: 0.7,
              borderRadius: "8px",
              boxShadow: "0 4px 12px rgba(29, 133, 198, 0.35)",
              minWidth: 0,
              maxWidth: "175px",
              flexShrink: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "inline-flex",
              transition: "all 0.25s ease",
              "&:hover": {
                background: "linear-gradient(135deg, #2F91CB 0%, #1874B0 100%)",
                boxShadow: "0 6px 16px rgba(29, 133, 198, 0.45)",
                transform: "translateY(-1px)",
              },
              "& .MuiButton-startIcon": {
                marginRight: "6px",
                flexShrink: 0,
              },
              "& .MuiButton-label, &": {
                overflow: "hidden",
              },
            }}
          >
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {title}
            </Box>
          </Button>
        </Tooltip>
        <Tooltip title="Copy download link">
          <IconButton
            size="small"
            onClick={handleCopyLink}
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              color: "#1D85C6",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(29, 133, 198, 0.16)",
                borderColor: "#1D85C6",
                transform: "translateY(-1px)",
              },
            }}
          >
            <ContentCopyIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title={titleInfo || "No release notes"}>
          <IconButton
            size="small"
            sx={{
              width: 28,
              height: 28,
              flexShrink: 0,
              color: "#6b7280",
              "&:hover": {
                backgroundColor: "rgba(107, 114, 128, 0.14)",
              },
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};
const Support = () => {
  const [isError, setIsError] = useState(errorInit);
  const [emailResponse, setEmailResponse] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [fromEmail, setFromEmail] = useState("");
  const fullNameInputRef = useRef();
  const subjectInputRef = useRef();
  const messageInputRef = useRef();
  const formRef = useRef();
  const { sendRequest } = useHttp();
  const [ticketId, setTicketId] = useState(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [openUploadModal, setOpenUploadModal] = useState(false);
  const [appData, setAppData] = useState(null);
  const handleUploadModalOpen = () => setOpenUploadModal(true);
  const handleUploadModalClose = () => setOpenUploadModal(false);

  // Function to trigger refresh
  const refreshAppData = () => {
    setIsRefresh((prev) => !prev); // Toggle the value to trigger the effect
  };
  useEffect(() => {
    sendRequest(
      {
        url: "/getAppUrl",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
          data: {
            applicationIDs: ["com.little.driver", "com.little.rider"],
          },
        },
      },
      getUrlController
    );
  }, [isRefresh]);

  const getUrlController = (data) => {
    if (data.status === 200 || data.status === 201) {
      setAppData(data);
    }
  };

  const querySubmitHandler = (e) => {
    e.preventDefault();
    // if (!validateInputs()) return;

    setIsLoading(true);

    sendRequest(
      {
        url: "/customer-support",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          from: fromEmail,
          fullName: fullNameInputRef.current.value,
          subject: subjectInputRef.current.value,
          message: messageInputRef.current.value,
        },
      },
      authenticateUser
    );

    setTimeout(() => setEmailResponse(null), 5000);
  };

  const authenticateUser = (data) => {
    if (data?.status === 200 || data?.status === 201) {
      formRef.current.reset();
      setEmailResponse({
        message:
          data?.message || "Your response has been successfully recorded.",
        success: true,
      });
      setIsLoading(false);
      // Set the ticket ID and open success dialog if ID exists
      if (data.id) {
        setTicketId(data.id);
        setOpenSuccessDialog(true);
      }
    } else {
      setIsLoading(false);
      throw new Error(data?.message || "Error occurred. Please try again.");
    }
  };

  const handleCloseSuccessDialog = () => {
    setOpenSuccessDialog(false);
  };

  return (
    <Box
      sx={{
        p: 2,
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
      }}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <PortalTitle fallback="support center" />
        {localStorage.getItem("user") === "upendra.shah@little.global" && (
          <Button
            variant="contained"
            startIcon={<CloudUploadIcon />}
            onClick={handleUploadModalOpen}
            sx={{
              backgroundColor: "primary.main",
              "&:hover": {
                backgroundColor: "primary.dark",
              },
            }}
          >
            Upload App
          </Button>
        )}
      </Box>

      <Grid container spacing={4}>
        {/* Left Side: QR Images (40%) */}
        <Grid item xs={10} md={6}>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap={4}
            sx={{
              p: 4,
              borderRadius: 2,
              background: "rgba(255,255,255,0.7)",
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.1)",
              backdropFilter: "blur(4px)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
          >
            <Typography variant="h6" color="text.secondary" textAlign="center">
              Scan QR codes to download our app
            </Typography>

            <Box
              display="flex"
              flexDirection="row"
              flexWrap="nowrap"
              gap={8}
              justifyContent="center"
              alignItems="flex-start"
              sx={{
                width: "100%",
                overflowX: "auto",
              }}
            >
              <QrCodeWithReveal
                value={appData?.rider?.url}
                title={
                  // appData?.rider?.versionName
                  //   ? `Rider (${appData.rider.versionName})`
                  //   : 
                    "Rider APK"
                }
                link={appData?.rider?.url}
                versionName={appData?.rider?.versionName || ""}
                titleInfo={appData?.rider?.releaseNote || ""}
              />

              <QrCodeWithReveal
                value={appData?.driver?.url}
                title={
                  // appData?.driver?.versionName
                  //   ? `Driver (${appData.driver.versionName})`
                  //   : 
                    "Driver APK"
                }
                link={appData?.driver?.url}
                versionName={appData?.driver?.versionName || ""}
                titleInfo={appData?.driver?.releaseNote || ""}
              />
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              mt={5}
              textAlign="center"
            >
              Available on Android platforms
            </Typography>
          </Box>
        </Grid>

        {/* Right Side: Form (60%) */}
        <Grid item xs={12} md={6}>
          <form
            ref={formRef}
            onSubmit={querySubmitHandler}
            style={{
              backgroundColor: "rgba(255,255,255,0.95)",
              padding: "32px",
              borderRadius: "12px",
              boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              color="primary.dark"
              mb={1}
            >
              Contact Us
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              mb={3}
              sx={{ opacity: 0.8 }}
            >
              Have any questions? We'd love to hear from you
            </Typography>

            <input
              type="hidden"
              name="orgName"
              defaultValue={localStorage.getItem("cpName")}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Full Name"
                  name="fullName"
                  variant="standard"
                  inputRef={fullNameInputRef}
                  error={!!isError.fullNameError}
                  helperText={isError.fullNameError}
                  onChange={() =>
                    setIsError((prev) => ({ ...prev, fullNameError: "" }))
                  }
                  margin="normal"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ddd",
                      },
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="fromEmail"
                  variant="standard"
                  value={fromEmail}
                  onChange={(e) => setFromEmail(e.target.value)}
                  margin="normal"
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: "#ddd",
                      },
                      "&:hover fieldset": {
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Subject"
              variant="standard"
              name="subject"
              inputRef={subjectInputRef}
              error={!!isError.subjectError}
              helperText={isError.subjectError}
              onChange={() =>
                setIsError((prev) => ({ ...prev, subjectError: "" }))
              }
              margin="normal"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ddd",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />

            <TextField
              fullWidth
              multiline
              rows={5}
              label="Your Message"
              name="message"
              inputRef={messageInputRef}
              onChange={() =>
                setIsError((prev) => ({ ...prev, messageError: "" }))
              }
              margin="normal"
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": {
                    borderColor: "#ddd",
                  },
                  "&:hover fieldset": {
                    borderColor: "primary.main",
                  },
                },
              }}
            />

            {emailResponse && (
              <Box
                mt={2}
                p={2}
                borderRadius={1}
                bgcolor={
                  emailResponse.success
                    ? "rgba(46, 125, 50, 0.1)"
                    : "rgba(211, 47, 47, 0.1)"
                }
                border={
                  emailResponse.success
                    ? "1px solid #2e7d32"
                    : "1px solid #d32f2f"
                }
              >
                <Typography
                  variant="body2"
                  color={emailResponse.success ? "success.dark" : "error.dark"}
                >
                  {emailResponse.message}
                </Typography>
              </Box>
            )}

            <Button
              type="submit"
              variant="contained"
              size="medium"
              color="success"
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Send Message"
              )}
            </Button>
          </form>
        </Grid>
      </Grid>

      {/* Ticket message after successfull email send */}
      <Dialog
        open={openSuccessDialog}
        onClose={handleCloseSuccessDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Support Ticket Created</Typography>
            <IconButton
              edge="end"
              onClick={handleCloseSuccessDialog}
              aria-label="close"
              sx={{
                color: "#d52029",
                "&:hover": {
                  backgroundColor: "rgba(213, 32, 41, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Typography variant="body1" gutterBottom>
              Thank you for contacting us! Your support ticket has been created
              successfully.
            </Typography>
            <Box mt={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
              <Typography variant="subtitle1">Ticket ID:</Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                {ticketId}
              </Typography>
            </Box>
            <Typography variant="body2" mt={2}>
              We'll get back to you soon. Please keep this reference number for
              future communication.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseSuccessDialog}
            color="primary"
            variant="contained"
            autoFocus
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <UploadApp
        open={openUploadModal}
        onClose={handleUploadModalClose}
        onSuccess={refreshAppData}
      />
    </Box>
  );
};

export default Support;
